import { useCallback } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircleIcon } from 'lucide-react';

import { PageTitle } from '@components/PageTitle';
import { EQueryKeys } from '@enums/queryKeys.enum';
import type { ETreatmentPriority } from '@enums/treatment.enum';
import { medicationSearchSchema } from '@schemas/medicationSearch.schema';
import { medicationUpdatesService } from '@services/medicationUpdates/service';

import { TreatmentFilters } from '../@components/TreatmentFilters';
import { TreatmentItem } from '../@components/TreatmentItem';

const medicationsQueryOptions = ({
  page,
  priority,
  title,
}: {
  page: number;
  priority?: ETreatmentPriority;
  title?: string;
}) =>
  queryOptions({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    queryKey: [EQueryKeys.Medications, page, title, priority],
    queryFn: () =>
      medicationUpdatesService.getAllAvailable({
        page,
        priority,
        title,
      }),
  });

export const Route = createFileRoute('/_layout/(private)/medications/')({
  component: Medications,
  validateSearch: search => medicationSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    page: search.page,
    priority: search.priority,
    title: search.title,
  }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(
      medicationsQueryOptions({
        page: deps.page,
        priority: deps.priority,
        title: deps.title,
      }),
    );
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircleIcon className="text-primary animate-spin" size={80} />
    </div>
  ),
});

function Medications() {
  const navigate = Route.useNavigate();
  const { page, priority, title } = Route.useSearch();

  const { queryClient } = Route.useRouteContext();

  const { data } = useQuery(medicationsQueryOptions({ page, priority, title }));

  const handleSearch = useCallback(
    (value: string) => {
      navigate({
        search: prev => ({
          ...prev,
          title: value,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePriorityChange = useCallback(
    (value?: ETreatmentPriority) => {
      navigate({
        search: prev => ({
          ...prev,
          priority: value,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="w-full space-y-3">
      <PageTitle title="Medicamentos disponíveis para revisão" />

      <TreatmentFilters
        onPriorityChange={handlePriorityChange}
        onSubmit={handleSearch}
        priority={priority}
        searchLabel="Pesquisar medicamento"
        searchPlaceholder="Ex: Amoxicilina"
      />

      <div className="grid w-full rounded-xl border-zinc-200 bg-zinc-200 shadow">
        {data?.list.map(medication => (
          <TreatmentItem
            key={medication.id}
            page="/medications"
            queryClient={queryClient}
            treatment={medication}
          />
        ))}
      </div>
    </div>
  );
}
